import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { User, UserFilter, PagedResult, Role } from '../../core';
import { UsersPageActions } from './users.actions';
import * as UsersSelectors from './users.selectors';

@Injectable({ providedIn: 'root' })
export class UsersFacade {
  private readonly store = inject(Store);

  // List observables
  readonly users$: Observable<PagedResult<User> | null> = this.store.select(
    UsersSelectors.selectUsers
  );
  readonly userItems$: Observable<User[]> = this.store.select(
    UsersSelectors.selectUserItems
  );
  readonly loading$: Observable<boolean> = this.store.select(
    UsersSelectors.selectUsersLoading
  );
  readonly error$: Observable<string | null> = this.store.select(
    UsersSelectors.selectUsersError
  );
  readonly currentFilter$: Observable<UserFilter> = this.store.select(
    UsersSelectors.selectCurrentFilter
  );
  readonly totalCount$: Observable<number> = this.store.select(
    UsersSelectors.selectTotalCount
  );
  readonly totalPages$: Observable<number> = this.store.select(
    UsersSelectors.selectTotalPages
  );
  readonly currentPage$: Observable<number> = this.store.select(
    UsersSelectors.selectCurrentPage
  );
  readonly pageSize$: Observable<number> = this.store.select(
    UsersSelectors.selectPageSize
  );

  // Roles
  readonly roles$: Observable<Role[]> = this.store.select(
    UsersSelectors.selectRoles
  );
  readonly rolesLoading$: Observable<boolean> = this.store.select(
    UsersSelectors.selectRolesLoading
  );

  // Actions
  loadUsers(filter?: Partial<UserFilter>): void {
    this.store.dispatch(UsersPageActions.loadUsers({ filter }));
  }

  search(searchTerm: string): void {
    this.store.dispatch(UsersPageActions.search({ searchTerm }));
  }

  sort(sortField: string): void {
    this.store.dispatch(UsersPageActions.sort({ sortField }));
  }

  changePage(page: number): void {
    this.store.dispatch(UsersPageActions.changePage({ page }));
  }

  changePageSize(pageSize: number): void {
    this.store.dispatch(UsersPageActions.changePageSize({ pageSize }));
  }

  filterByStatus(status: boolean | null): void {
    this.store.dispatch(UsersPageActions.filterByStatus({ status }));
  }

  filterByRole(roleName: string | null): void {
    this.store.dispatch(UsersPageActions.filterByRole({ roleName }));
  }

  clearFilters(): void {
    this.store.dispatch(UsersPageActions.clearFilters());
  }

  refresh(): void {
    this.store.dispatch(UsersPageActions.refresh());
  }

  loadRoles(): void {
    this.store.dispatch(UsersPageActions.loadRoles());
  }
}
